/**
 * Activity Log 관련 상수 정의
 * 백엔드와 프론트엔드 간 일관성을 보장하기 위한 중앙 관리
 */

// 활동 카테고리 (백엔드 ActivityCategory와 일치)
export const ACTIVITY_CATEGORY = {
  ALL: 'all',
  USER_ACTIVITY: 'user_activity',  // 일반 사용자 활동
  ADMIN_ACTIVITY: 'admin_activity', // 관리자 활동
  SYSTEM: 'system',  // 시스템 로그
} as const;

// 활동 카테고리 라벨 (한글)
export const ACTIVITY_CATEGORY_LABELS: Record<string, string> = {
  [ACTIVITY_CATEGORY.ALL]: '전체',
  [ACTIVITY_CATEGORY.USER_ACTIVITY]: '사용자 활동',
  [ACTIVITY_CATEGORY.ADMIN_ACTIVITY]: '관리자 활동',
  [ACTIVITY_CATEGORY.SYSTEM]: '시스템',
};

// 활동 유형 (백엔드 ActivityType과 일치)
export const ACTIVITY_TYPE = {
  // 인증 관련 (USER_ACTIVITY)
  LOGIN: 'login',
  LOGOUT: 'logout',
  LOGIN_FAILED: 'login_failed',
  GOOGLE_OAUTH_LOGIN: 'google_oauth_login',
  GOOGLE_OAUTH_SIGNUP: 'google_oauth_signup',

  // 비밀번호 관련 (USER_ACTIVITY)
  PASSWORD_CHANGE: 'password_change',
  PASSWORD_RESET_REQUEST: 'password_reset_request',
  PASSWORD_RESET_CONFIRM: 'password_reset_confirm',

  // 서비스 접근 (USER_ACTIVITY)
  SERVICE_ACCESS: 'service_access',
  SERVICE_SUBSCRIBE: 'service_subscribe',
  SERVICE_UNSUBSCRIBE: 'service_unsubscribe',

  // 결제 및 크레딧 (USER_ACTIVITY)
  CREDIT_PURCHASE: 'credit_purchase',
  CREDIT_USE: 'credit_use',
  PAYMENT_INITIATE: 'payment_initiate',
  PAYMENT_CONFIRM: 'payment_confirm',
  PAYMENT_COMPLETE: 'payment_complete',
  PAYMENT_CANCEL: 'payment_cancel',

  // 구독 결제 (USER_ACTIVITY)
  SUBSCRIPTION_PAYMENT_SUCCESS: 'subscription_payment_success',
  SUBSCRIPTION_PAYMENT_FAILED: 'subscription_payment_failed',
  SUBSCRIPTION_PAYMENT_RETRY_SUCCESS: 'subscription_payment_retry_success',
  SUBSCRIPTION_PAYMENT_ALL_FAILED: 'subscription_payment_all_failed',

  // 사용자 관리 (ADMIN_ACTIVITY) - 슈퍼관리자만
  USER_SIGNUP: 'user_signup',
  USER_APPROVE: 'user_approve',
  USER_DEACTIVATE: 'user_deactivate',
  USER_ACTIVATE: 'user_activate',
  USER_DELETE: 'user_delete',
  USER_UPDATE: 'user_update',
  USER_INVITE: 'user_invite',
  ADMIN_PASSWORD_RESET: 'admin_password_reset',

  // 고객사 관리 (ADMIN_ACTIVITY) - 슈퍼관리자만
  CUSTOMER_CREATE: 'customer_create',
  CUSTOMER_UPDATE: 'customer_update',
  CUSTOMER_DEACTIVATE: 'customer_deactivate',
  CUSTOMER_ACTIVATE: 'customer_activate',
  CUSTOMER_DELETE: 'customer_delete',

  // 크레딧 관리 (ADMIN_ACTIVITY) - 슈퍼관리자만
  CREDIT_GRANT: 'credit_grant',
  CREDIT_DEDUCT: 'credit_deduct',

  // 시스템 설정 (SYSTEM) - 슈퍼관리자만
  SETTINGS_UPDATE: 'settings_update',

  // MSA 서비스 관리 (SYSTEM) - 슈퍼관리자만
  SERVICE_REGISTER: 'service_register',
  SERVICE_APPROVE: 'service_approve',
  SERVICE_REJECT: 'service_reject',
  SERVICE_STARTUP: 'service_startup',
  SERVICE_SHUTDOWN: 'service_shutdown',
  NGINX_CONFIG_ERROR: 'nginx_config_error',
} as const;

// 활동 유형 라벨 (한글)
export const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  // 인증 관련
  [ACTIVITY_TYPE.LOGIN]: '로그인',
  [ACTIVITY_TYPE.LOGOUT]: '로그아웃',
  [ACTIVITY_TYPE.LOGIN_FAILED]: '로그인 실패',
  [ACTIVITY_TYPE.GOOGLE_OAUTH_LOGIN]: 'Google 로그인',
  [ACTIVITY_TYPE.GOOGLE_OAUTH_SIGNUP]: 'Google 회원가입',

  // 비밀번호 관련
  [ACTIVITY_TYPE.PASSWORD_CHANGE]: '비밀번호 변경',
  [ACTIVITY_TYPE.PASSWORD_RESET_REQUEST]: '비밀번호 재설정 요청',
  [ACTIVITY_TYPE.PASSWORD_RESET_CONFIRM]: '비밀번호 재설정 확인',
  [ACTIVITY_TYPE.ADMIN_PASSWORD_RESET]: '관리자 비밀번호 재설정',

  // 서비스 접근
  [ACTIVITY_TYPE.SERVICE_ACCESS]: '서비스 접근',
  [ACTIVITY_TYPE.SERVICE_SUBSCRIBE]: '서비스 구독',
  [ACTIVITY_TYPE.SERVICE_UNSUBSCRIBE]: '서비스 구독 해제',

  // 결제 및 크레딧
  [ACTIVITY_TYPE.CREDIT_PURCHASE]: '크레딧 구매',
  [ACTIVITY_TYPE.CREDIT_USE]: '크레딧 사용',
  [ACTIVITY_TYPE.PAYMENT_INITIATE]: '결제 시작',
  [ACTIVITY_TYPE.PAYMENT_CONFIRM]: '결제 확인',
  [ACTIVITY_TYPE.PAYMENT_COMPLETE]: '결제 완료',
  [ACTIVITY_TYPE.PAYMENT_CANCEL]: '결제 취소',

  // 구독 결제
  [ACTIVITY_TYPE.SUBSCRIPTION_PAYMENT_SUCCESS]: '구독 결제 성공',
  [ACTIVITY_TYPE.SUBSCRIPTION_PAYMENT_FAILED]: '구독 결제 실패',
  [ACTIVITY_TYPE.SUBSCRIPTION_PAYMENT_RETRY_SUCCESS]: '구독 결제 재시도 성공',
  [ACTIVITY_TYPE.SUBSCRIPTION_PAYMENT_ALL_FAILED]: '구독 결제 모든 카드 실패',

  // 사용자 관리
  [ACTIVITY_TYPE.USER_SIGNUP]: '회원가입',
  [ACTIVITY_TYPE.USER_APPROVE]: '사용자 승인',
  [ACTIVITY_TYPE.USER_DEACTIVATE]: '사용자 비활성화',
  [ACTIVITY_TYPE.USER_ACTIVATE]: '사용자 활성화',
  [ACTIVITY_TYPE.USER_DELETE]: '사용자 삭제',
  [ACTIVITY_TYPE.USER_UPDATE]: '사용자 수정',
  [ACTIVITY_TYPE.USER_INVITE]: '사용자 초대',

  // 고객사 관리
  [ACTIVITY_TYPE.CUSTOMER_CREATE]: '고객사 생성',
  [ACTIVITY_TYPE.CUSTOMER_UPDATE]: '고객사 수정',
  [ACTIVITY_TYPE.CUSTOMER_DEACTIVATE]: '고객사 비활성화',
  [ACTIVITY_TYPE.CUSTOMER_ACTIVATE]: '고객사 활성화',
  [ACTIVITY_TYPE.CUSTOMER_DELETE]: '고객사 삭제',

  // 크레딧 관리
  [ACTIVITY_TYPE.CREDIT_GRANT]: '크레딧 지급',
  [ACTIVITY_TYPE.CREDIT_DEDUCT]: '크레딧 차감',

  // 시스템 설정
  [ACTIVITY_TYPE.SETTINGS_UPDATE]: '설정 변경',

  // MSA 서비스 관리
  [ACTIVITY_TYPE.SERVICE_REGISTER]: '서비스 등록',
  [ACTIVITY_TYPE.SERVICE_APPROVE]: '서비스 승인',
  [ACTIVITY_TYPE.SERVICE_REJECT]: '서비스 거부',
  [ACTIVITY_TYPE.SERVICE_STARTUP]: '시스템 부팅',
  [ACTIVITY_TYPE.SERVICE_SHUTDOWN]: '시스템 종료',
  [ACTIVITY_TYPE.NGINX_CONFIG_ERROR]: 'Nginx 설정 오류',
};

// 사용자 활동 유형 (일반 사용자/기업 관리자가 볼 수 있는 것)
const USER_ACTIVITY_TYPES = [
  ACTIVITY_TYPE.LOGIN,
  ACTIVITY_TYPE.LOGOUT,
  ACTIVITY_TYPE.LOGIN_FAILED,
  ACTIVITY_TYPE.GOOGLE_OAUTH_LOGIN,
  ACTIVITY_TYPE.GOOGLE_OAUTH_SIGNUP,
  ACTIVITY_TYPE.PASSWORD_CHANGE,
  ACTIVITY_TYPE.PASSWORD_RESET_REQUEST,
  ACTIVITY_TYPE.PASSWORD_RESET_CONFIRM,
  ACTIVITY_TYPE.SERVICE_ACCESS,
  ACTIVITY_TYPE.SERVICE_SUBSCRIBE,
  ACTIVITY_TYPE.SERVICE_UNSUBSCRIBE,
  ACTIVITY_TYPE.CREDIT_PURCHASE,
  ACTIVITY_TYPE.CREDIT_USE,
  ACTIVITY_TYPE.PAYMENT_INITIATE,
  ACTIVITY_TYPE.PAYMENT_CONFIRM,
  ACTIVITY_TYPE.PAYMENT_COMPLETE,
  ACTIVITY_TYPE.PAYMENT_CANCEL,
  ACTIVITY_TYPE.SUBSCRIPTION_PAYMENT_SUCCESS,
  ACTIVITY_TYPE.SUBSCRIPTION_PAYMENT_FAILED,
  ACTIVITY_TYPE.SUBSCRIPTION_PAYMENT_RETRY_SUCCESS,
  ACTIVITY_TYPE.SUBSCRIPTION_PAYMENT_ALL_FAILED,
];

// 관리자 활동 유형 (슈퍼관리자만)
const ADMIN_ACTIVITY_TYPES = [
  ACTIVITY_TYPE.USER_SIGNUP,
  ACTIVITY_TYPE.USER_APPROVE,
  ACTIVITY_TYPE.USER_DEACTIVATE,
  ACTIVITY_TYPE.USER_ACTIVATE,
  ACTIVITY_TYPE.USER_DELETE,
  ACTIVITY_TYPE.USER_UPDATE,
  ACTIVITY_TYPE.USER_INVITE,
  ACTIVITY_TYPE.ADMIN_PASSWORD_RESET,
  ACTIVITY_TYPE.CUSTOMER_CREATE,
  ACTIVITY_TYPE.CUSTOMER_UPDATE,
  ACTIVITY_TYPE.CUSTOMER_DEACTIVATE,
  ACTIVITY_TYPE.CUSTOMER_ACTIVATE,
  ACTIVITY_TYPE.CUSTOMER_DELETE,
  ACTIVITY_TYPE.CREDIT_GRANT,
  ACTIVITY_TYPE.CREDIT_DEDUCT,
];

// 시스템 로그 유형 (슈퍼관리자만)
const SYSTEM_LOG_TYPES = [
  ACTIVITY_TYPE.SETTINGS_UPDATE,
  ACTIVITY_TYPE.SERVICE_REGISTER,
  ACTIVITY_TYPE.SERVICE_APPROVE,
  ACTIVITY_TYPE.SERVICE_REJECT,
  ACTIVITY_TYPE.SERVICE_STARTUP,
  ACTIVITY_TYPE.SERVICE_SHUTDOWN,
  ACTIVITY_TYPE.NGINX_CONFIG_ERROR,
];

// 프론트엔드용 필터 옵션 - 사용자용 (일반 사용자/기업 관리자)
export const userActivityTypes = [
  { value: 'all', label: '전체' },
  ...USER_ACTIVITY_TYPES.map(value => ({
    value,
    label: ACTIVITY_TYPE_LABELS[value] || value,
  }))
];

// 프론트엔드용 필터 옵션 - 슈퍼관리자용 (전체 이용자 로그)
// 전체 이용자 로그는 일반 사용자 활동만 표시 (시스템/관리자 활동 제외)
export const superAdminUserLogTypes = [
  { value: 'all', label: '전체' },
  ...USER_ACTIVITY_TYPES.map(value => ({
    value,
    label: ACTIVITY_TYPE_LABELS[value] || value,
  }))
];

// 프론트엔드용 필터 옵션 - 슈퍼관리자용 (시스템 로그)
export const superAdminSystemLogTypes = [
  { value: 'all', label: '전체' },
  ...ADMIN_ACTIVITY_TYPES.map(value => ({
    value,
    label: ACTIVITY_TYPE_LABELS[value] || value,
  })),
  ...SYSTEM_LOG_TYPES.map(value => ({
    value,
    label: ACTIVITY_TYPE_LABELS[value] || value,
  }))
];

// 카테고리 필터 옵션
export const activityCategories = [
  { value: 'all', label: '전체' },
  { value: ACTIVITY_CATEGORY.USER_ACTIVITY, label: ACTIVITY_CATEGORY_LABELS[ACTIVITY_CATEGORY.USER_ACTIVITY] },
  { value: ACTIVITY_CATEGORY.ADMIN_ACTIVITY, label: ACTIVITY_CATEGORY_LABELS[ACTIVITY_CATEGORY.ADMIN_ACTIVITY] },
  { value: ACTIVITY_CATEGORY.SYSTEM, label: ACTIVITY_CATEGORY_LABELS[ACTIVITY_CATEGORY.SYSTEM] },
];

// 하위 호환성을 위한 기본 export (deprecated)
export const activityTypes = userActivityTypes;
